import * as React from 'react'

interface PinInputProps {
  /** Number of digits */
  length?: number
  /** Current value */
  value: string
  /** Called with the new value whenever it changes */
  onChange: (value: string) => void
  /** Called when all digits are filled (via typing or paste) */
  onComplete?: (value: string) => void
  /** Disable all inputs */
  disabled?: boolean
  /** Auto-focus the first input on mount */
  autoFocus?: boolean
  /** Show a visual separator at this index (e.g. 3 for XXX-XXX) */
  separatorAfter?: number
  /** Allowed character pattern (default: alphanumeric) */
  pattern?: RegExp
  /** Additional class name for the container */
  className?: string
}

export const PinInput: React.FC<PinInputProps> = ({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled = false,
  autoFocus = false,
  separatorAfter = Math.floor(length / 2),
  pattern = /^[A-Z0-9]$/,
  className,
}) => {
  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([])
  const pendingComplete = React.useRef(false)

  React.useEffect(() => {
    if (pendingComplete.current && value.length === length) {
      pendingComplete.current = false
      onComplete?.(value)
    }
  }, [value, length, onComplete])

  const setValue = (next: string, shouldComplete = false) => {
    onChange(next)
    if (shouldComplete && next.length === length) pendingComplete.current = true
  }

  const handleChange = (index: number, inputValue: string) => {
    const cleaned = inputValue.toUpperCase().replace(
      new RegExp(`[^${pattern.source.slice(1, -1)}]`, 'g'), ''
    )

    // Multi-char input (autofill / paste-like)
    if (cleaned.length > 1) {
      const filled = value.slice(0, index) + cleaned
      const final = filled.slice(0, length)
      setValue(final, true)
      inputRefs.current[Math.min(final.length, length - 1)]?.focus()
      return
    }

    const char = cleaned.slice(-1)
    const chars = value.padEnd(length, ' ').split('')
    chars[index] = char || ''
    const updated = chars.map(c => c === ' ' ? '' : c).join('').slice(0, length)
    setValue(updated, char !== '' && updated.length === length)

    if (char && index < length - 1) inputRefs.current[index + 1]?.focus()
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        const chars = value.padEnd(length, ' ').split('')
        chars[index - 1] = ''
        setValue(chars.map(c => c === ' ' ? '' : c).join('').replace(/\s+$/, ''))
        inputRefs.current[index - 1]?.focus()
        e.preventDefault()
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
      e.preventDefault()
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus()
      e.preventDefault()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData
      .getData('text')
      .toUpperCase()
      .replace(new RegExp(`[^${pattern.source.slice(1, -1)}]`, 'g'), '')
      .slice(0, length)
    setValue(pasted, true)
    inputRefs.current[Math.min(pasted.length, length - 1)]?.focus()
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select()
  }

  const indices = Array.from({ length }, (_, i) => i)

  return (
    <div className={`flex gap-2 justify-center items-center ${className ?? ''}`}>
      {indices.map(index => (
        <React.Fragment key={index}>
          {index === separatorAfter && (
            <span className="w-2 h-0.5 bg-gray-300 rounded-full mx-0.5" />
          )}
          <input
            ref={el => { inputRefs.current[index] = el }}
            type="text"
            inputMode="text"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            value={value[index] || ''}
            onChange={e => handleChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={handleFocus}
            autoFocus={autoFocus && index === 0}
            disabled={disabled}
            className="w-12 h-14 border border-gray-300 rounded-lg text-xl font-medium
              text-center uppercase bg-transparent outline-none transition-all
              focus:border-brand focus:ring-2 focus:ring-brand/20
              disabled:opacity-50 touch-manipulation"
          />
        </React.Fragment>
      ))}
    </div>
  )
}
